import "./zod-config"; // first: before any module parses with zod
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import "@fontsource-variable/inter";
import "@fontsource-variable/geist-mono";
import "./styles.css";
import { ApiError } from "./api/http";
import { keys } from "./api/queries";
import { createAppRouter } from "./router";
import { ThemeProvider } from "./lib/theme";

// A 401 anywhere means the session ended: drop cached identity and let the route guards redirect.
const onAuthError = (err: unknown) => {
  if (err instanceof ApiError && err.status === 401 && err.code !== "invalid_credentials") {
    queryClient.setQueryData(keys.me, null);
    void router.navigate({ to: "/sign-in", search: { redirect: window.location.pathname } });
  }
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onAuthError }),
  mutationCache: new MutationCache({ onError: onAuthError }),
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createAppRouter(queryClient);

const root = document.getElementById("root");
if (!root) throw new Error("#root missing");

createRoot(root).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
